import { useEffect, useState, useCallback } from 'react';

/**
 * Hook exposing the browser `beforeinstallprompt` event.
 * Returns { canInstall, installed, promptInstall }.
 */
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect if the app is already running as an installed PWA
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return null;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice?.outcome;
    } catch {
      setDeferred(null);
      return null;
    }
  }, [deferred]);

  return { canInstall: !!deferred && !installed, installed, promptInstall };
}
